/* eslint-disable consistent-return */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import { useIntl } from 'react-intl';
import { padStart } from '../../lib/utils';

import RoundedButton from '../buttons/Rounded';
// import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/blocks/countdown.module.scss';

const propTypes = {
    buttonColor: PropTypes.string,
    endDate: PropTypes.string,
    label: PropTypes.string,
    link: PropTypes.shape({
        external: PropTypes.bool,
        label: PropTypes.string,
        url: PropTypes.string,
    }),
    className: PropTypes.string,
    isContainer: PropTypes.bool,
};

const defaultProps = {
    buttonColor: '#222',
    endDate: null,
    label: null,
    link: null,
    className: null,
    isContainer: false,
};

const CountdownBlock = ({ className, buttonColor, endDate, label, link, isContainer }) => {
    const intl = useIntl();
    const { external = false, url = null, label: buttonLabel = null } = link || {};
    const [timer, setTimer] = useState(0);
    const [counting, setCounting] = useState(true);

    const targetDate = useMemo(
        () => (typeof endDate === 'string' ? parseISO(endDate) : endDate),
        [endDate],
    );
    const [remainingTime, setRemainingTime] = useState(null);
    const dayLabel = intl.formatMessage({
        defaultMessage: 'day',
        description: 'Date label',
    });

    useEffect(() => {
        const now = new Date();
        const diff = differenceInSeconds(targetDate, now);
        if (diff < 0) {
            setRemainingTime(`00:00:00`);
        } else {
            const days = Math.floor(diff / (3600 * 24));
            const hours = Math.floor((diff % (3600 * 24)) / 3600);
            const minutes = Math.floor((diff % 3600) / 60);
            const seconds = Math.floor((diff % 3600) % 60);
            const finalDaysLabel = days > 1 ? `${dayLabel}s` : dayLabel;
            setRemainingTime(
                `${days > 0 ? `${days} ${finalDaysLabel} ` : ''}${padStart(
                    hours,
                    2,
                    '0',
                )}:${padStart(minutes, 2, '0')}:${padStart(seconds, 2, '0')}`,
            );
        }
    }, [targetDate, timer, dayLabel]);

    useEffect(() => {
        let intervalId = null;
        if (counting) {
            intervalId = setInterval(() => {
                const now = new Date();
                const diff = differenceInSeconds(targetDate, now);
                if (diff < 0) {
                    setCounting(false);
                }
                setTimer((t) => t + 1);
            }, 1000);
        } else {
            return () => clearInterval(intervalId);
        }
        return () => clearInterval(intervalId);
    }, [targetDate, setTimer, setCounting]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.isContainer]: isContainer,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.inner}>
                {label !== null ? <p className={styles.label}>{label}</p> : null}
                {remainingTime !== null ? (
                    <span className={styles.countdown}>{remainingTime}</span>
                ) : null}
                <RoundedButton
                    backgroundColor={buttonColor}
                    external={external}
                    href={url}
                    className={styles.button}
                >
                    {buttonLabel}
                </RoundedButton>
            </div>
        </div>
    );
};

CountdownBlock.propTypes = propTypes;
CountdownBlock.defaultProps = defaultProps;

export default CountdownBlock;
